import React, { createContext, useState, ReactNode, useContext, useEffect, useRef } from 'react';
import { Dispatch, SetStateAction } from 'react';
import { Form, useParams } from 'react-router-dom';
import { Hooks } from 'hooks/commonHooks';
import { MessageType } from 'models/BaseModel';
import { Code } from 'helpers/CalendarImports';

interface FormContextProps {
  keyId: string;
  setKeyId: Dispatch<SetStateAction<string>>;
  activeId: number | undefined;
  setActiveId: (activeId: number | undefined) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  hasUnsaved: boolean;
  setHasUnsaved: (loading: boolean) => void;
  showMenu: boolean;
  setShowMenu: (showMenu: boolean) => void;
  confirm: MessageType; 
  setConfirm: Dispatch<SetStateAction<MessageType>>;
  message: MessageType;
  setMessage: Dispatch<SetStateAction<MessageType>>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  error: string | null;
  setError: (error: string | null) => void;
  menuPosition: any;
  setMenuPosition: any;
  iframeRef: any;
  iframeRef2: any;
  contextMenuRef: any;
  formRef: React.RefObject<HTMLFormElement>;
}

const FormContext = createContext<FormContextProps | undefined>(undefined);

export const FormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();
  const [keyId, setKeyId] = useState<string>(paramKeyId || '');
  const [activeId, setActiveId] = useState<number | undefined>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasUnsaved, setHasUnsaved] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<MessageType>(Code.Message.None);
  const [message, setMessage] = useState<MessageType>(Code.Message.None);
  const [showMenu, setShowMenu] = useState<boolean>(false); 
  const [activeTab, setActiveTab] = useState<string>("tab1");
  const [error, setError] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = Hooks.Form.Position(); 
  const formRef = useRef<HTMLFormElement | null>(null);

  const iframeRef = Hooks.Form.Frame();
  const iframeRef2 = Hooks.Form.Frame();
  const contextMenuRef = Hooks.Form.Div();

  useEffect(() => {
    if (paramKeyId) {
      setKeyId(paramKeyId);
    }
  }, [paramKeyId]);

  return (
    <FormContext.Provider value={{ 
      keyId, setKeyId,
      activeId, setActiveId, 
      loading, setLoading, 
      hasUnsaved, setHasUnsaved, 
      message, setMessage, 
      showMenu, setShowMenu, 
      confirm, setConfirm, 
      activeTab, setActiveTab, 
      error, setError, 
      menuPosition, setMenuPosition, 
      formRef, 
      iframeRef, 
      iframeRef2, 
      contextMenuRef  
    }}>
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};